import { useEffect, useState } from 'react';
import { AnimationShell, Button, Typography, Link } from '@/components';
import { useTranslation } from 'react-i18next';
import { Route } from '@/constants';
import { useNavigate } from 'react-router-dom';
import { useTokenParam } from '@/hooks';
import { useVerifyUserMutation, useRequestTokenMutation } from '@/features/auth';
import { jwtDecode } from 'jwt-decode';
import { BASE_URL } from '@/config';

const MESSAGES = {
  verifying: 'Verifying your account...',
  invalidToken: 'The verification token is not valid.',
  verified: 'Your account has been verified!',
  verificationFailed: 'Verification failed. Please try again later.',
  checkEmail: 'Check your email inbox to verify your account',
  stepAway:
    "You're just one step away from accessing dozens of renewable energy projects, advisors and market insights.",
  loginInfo: 'Please, login to start using your account.',
  requestToken: 'Please request a new token by clicking the button below.',
  requesting: 'Requesting...',
  newToken: 'New token',
  requestError: 'There was an error requesting a new token. Please try again later',
  suspended: 'The account could not be verified.',
  suspendedInfoError: 'If you think this is a mistake, please contact us at',
};

export function VerifyAccountPage() {
  const [isTokenInvalid, setIsTokenInvalid] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [isSuspended, setIsSuspended] = useState(false);

  const { t } = useTranslation(['verify']);
  const navigate = useNavigate();
  const token = useTokenParam();

  const [mutation, { isLoading, isError, isSuccess }] = useVerifyUserMutation();
  const [
    requestToken,
    { isLoading: isRequestTokenLoading, isError: isRequestTokenError, isSuccess: isRequestTokenSuccess },
  ] = useRequestTokenMutation();

  const decodeToken = jwtDecode(token);
  const email = 'email' in decodeToken ? (decodeToken.email as string) : null;

  useEffect(() => {
    if (token && !isLoading && !hasFetched) {
      setHasFetched(true);
      mutation({ token })
        .unwrap()
        .catch((err) => {
          if (err.status === 401) {
            setIsTokenInvalid(true);
          } else if (err.status === 403) {
            setIsSuspended(true);
            setIsTokenInvalid(true);
          }
        });
    }
  }, [token, mutation, hasFetched, isLoading]);

  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        setShowButton(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isSuccess]);

  const handleButtonClick = () => {
    navigate(Route.SignIn);
  };

  const handleRequestNewToken = async () => {
    if (!email) return;

    await requestToken({
      email,
      url: `${BASE_URL}${Route.VerifyEmailAddress}`,
    })
      .unwrap()
      .then()
      .catch((error) => console.error('Error requesting token:', error));
  };

  const getMainMessage = () => {
    if (isRequestTokenSuccess) return '';
    if (isSuspended) return t(MESSAGES.suspended);
    if (isLoading) return t(MESSAGES.verifying);
    if (isTokenInvalid) return t(MESSAGES.invalidToken);
    if (isSuccess) return t(MESSAGES.verified);
    if (isError) return t(MESSAGES.verificationFailed);
    return '';
  };

  return (
    <div className="w-full space-y-8">
      <div className="space-y-4">
        {getMainMessage() && (
          <Typography as="h1" size="heading-2" color="primary" weight="semibold" className="text-center">
            {getMainMessage()}
          </Typography>
        )}

        {!isSuspended && isRequestTokenSuccess ? (
          <>
            <Typography as="h1" size="heading-1" color="primary" weight="semibold" className="text-center">
              {t(MESSAGES.checkEmail)}
            </Typography>
            <Typography size="body-2" color="secondary" className="text-center">
              {t(MESSAGES.stepAway)}
            </Typography>
          </>
        ) : (
          !isSuspended && (
            <>
              {isSuccess && (
                <Typography size="body-2" color="secondary" className="text-center">
                  {t(MESSAGES.loginInfo)}
                </Typography>
              )}
              {isTokenInvalid && (
                <Typography size="body-2" color="secondary" className="text-center">
                  {t(MESSAGES.requestToken)}
                </Typography>
              )}
            </>
          )
        )}
      </div>

      {!isSuspended && isSuccess && showButton && !isRequestTokenSuccess && (
        <AnimationShell type="opacity" delay={0.35} className="inline-flex w-full items-center justify-center gap-x-1">
          <Button onClick={handleButtonClick} color="primary" size="lg" className="w-52">
            {t('Login')}
          </Button>
        </AnimationShell>
      )}

      {!isSuspended && isTokenInvalid && !isRequestTokenSuccess && (
        <div className="inline-flex w-full items-center justify-center gap-x-1">
          <Button onClick={handleRequestNewToken} disabled={isRequestTokenLoading}>
            {isRequestTokenLoading ? t(MESSAGES.requesting) : t(MESSAGES.newToken)}
          </Button>
        </div>
      )}

      {!isSuspended && isRequestTokenError && (
        <div className="inline-flex w-full items-center justify-center gap-x-1">
          <Typography weight="normal" size="body-2" className="text-center">
            {t(MESSAGES.requestError)} or email us at <Link to="mailto:clients@nteaser.com">clients@nteaser.com</Link>
          </Typography>
        </div>
      )}

      {isSuspended && (
        <div className="inline-flex w-full items-center justify-center gap-x-1">
          <Typography weight="normal" size="body-2" className="text-center">
            {t(MESSAGES.suspendedInfoError)} <Link to="mailto:clients@nteaser.com">clients@nteaser.com</Link>
          </Typography>
        </div>
      )}
    </div>
  );
}
